import React from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AlertTriangle, CheckCircle, Clock, MapPin, Shield, ArrowRight, Activity, Info } from 'lucide-react';
import { SafetyAlert, SafetyStats, RiskZone, SafetyRecommendation, AlertHistoryItem } from './types';

interface DashboardTabProps {
  alerts: SafetyAlert[];
  stats: SafetyStats;
  riskZones: RiskZone[];
  recommendations: SafetyRecommendation[];
  alertHistory: AlertHistoryItem[];
  onAcknowledgeAlert: (alertId: string) => void;
  onViewAlertDetails: (alertId: string) => void;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ 
  alerts, 
  stats, 
  riskZones, 
  recommendations, 
  alertHistory,
  onAcknowledgeAlert,
  onViewAlertDetails
}) => {
  // Colors for charts
  const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6'];
  
  // Format timestamp as relative time
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };
  
  // Get color based on severity or status
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'HIGH': return 'text-red-500';
      case 'MEDIUM': return 'text-yellow-500';
      case 'LOW': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'text-red-500';
      case 'ACKNOWLEDGED': return 'text-yellow-500';
      case 'RESOLVED': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };
  
  // Get severity badge style
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'HIGH': return 'bg-red-100 text-red-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get active/high priority alerts
  const activeAlerts = alerts
    .filter(alert => alert.status === 'ACTIVE')
    .sort((a, b) => {
      // Sort by severity then by timestamp
      const severityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
      const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
      
      if (severityDiff !== 0) return severityDiff;
      return b.timestamp.getTime() - a.timestamp.getTime();
    })
    .slice(0, 5); // Show top 5
    
  // Get high risk zones
  const highRiskZones = riskZones
    .filter(zone => zone.riskLevel === 'HIGH')
    .sort((a, b) => b.riskScore - a.riskScore);
    
  // Prepare chart data
  const alertsOverTime = [
    { day: 'Mon', count: 5, highSeverity: 2 },
    { day: 'Tue', count: 7, highSeverity: 3 },
    { day: 'Wed', count: 4, highSeverity: 1 },
    { day: 'Thu', count: 9, highSeverity: 4 },
    { day: 'Fri', count: 6, highSeverity: 2 },
    { day: 'Sat', count: 3, highSeverity: 1 },
    { day: 'Sun', count: 2, highSeverity: 0 }
  ];
  
  return (
    <div>
      {/* Main Dashboard Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column: Active Alerts & Recommendations */}
        <div className="lg:col-span-1 space-y-6">
          {/* Active Alerts */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Active Alerts</h3>
              {stats.activeAlerts > 0 && (
                <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                  {stats.activeAlerts} Active
                </span>
              )}
            </div>
            
            {activeAlerts.length > 0 ? (
              <div className="space-y-3">
                {activeAlerts.map(alert => (
                  <div key={alert.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start">
                      <AlertTriangle className={`${getSeverityColor(alert.severity)} mt-1 mr-2 flex-shrink-0`} size={18} />
                      <div className="flex-grow">
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(alert.severity)}`}>
                            {alert.severity}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(alert.timestamp)}
                          </span>
                        </div>
                        <p className="mt-1 text-sm font-medium">{alert.description}</p>
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          <MapPin size={12} className="mr-1" />
                          <span>{alert.location.room}</span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <button
                            onClick={() => onAcknowledgeAlert(alert.id)}
                            className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded hover:bg-blue-200"
                          >
                            Acknowledge
                          </button>
                          <button
                            onClick={() => onViewAlertDetails(alert.id)}
                            className="flex items-center text-blue-600 text-xs"
                          >
                            Details <ArrowRight size={12} className="ml-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                <p className="text-gray-600">No active alerts at this time</p>
              </div>
            )}
            
            {stats.activeAlerts > 5 && (
              <button
                onClick={() => onViewAlertDetails('')}
                className="w-full mt-3 px-3 py-2 bg-gray-100 text-blue-600 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                View All {stats.activeAlerts} Alerts
              </button>
            )}
          </div>
          
          {/* High Priority Recommendations */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Safety Recommendations</h3>
              {recommendations.length > 0 && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                  {recommendations.length} Pending
                </span>
              )}
            </div>
            
            {recommendations.length > 0 ? (
              <div className="space-y-3">
                {recommendations.map(recommendation => (
                  <div key={recommendation.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start">
                      <Shield className="text-blue-500 mt-1 mr-2 flex-shrink-0" size={18} />
                      <div>
                        <div className="flex items-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityBadge(recommendation.priority)}`}>
                            {recommendation.priority}
                          </span>
                        </div>
                        <p className="mt-1 text-sm font-medium">{recommendation.title}</p>
                        <p className="mt-1 text-xs text-gray-600">{recommendation.description}</p>
                        <div className="mt-2">
                          <span className="text-xs text-gray-500">Impact: </span>
                          <span className={`text-xs font-medium ${
                            recommendation.impact > 80 ? 'text-green-600' :
                            recommendation.impact > 60 ? 'text-yellow-600' :
                            'text-blue-600'
                          }`}>
                            {recommendation.impact}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Shield className="mx-auto text-green-500 mb-2" size={32} />
                <p className="text-gray-600">No high priority recommendations</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Middle Column: Charts */}
        <div className="lg:col-span-1 space-y-6">
          {/* Alerts by Type Chart */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Alerts by Type</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.alertsByType}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#3b82f6" name="Total Alerts" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-sm text-gray-600">
              <p className="flex items-start">
                <Info size={16} className="text-blue-500 mr-1 mt-0.5" />
                <span>
                  {(() => {
                    const maxType = [...stats.alertsByType].sort((a, b) => b.count - a.count)[0];
                    return `${maxType.type} alerts are most common (${maxType.count}).`;
                  })()}
                </span>
              </p>
            </div>
          </div>
          
          {/* Alerts by Time of Day */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Alerts by Time of Day</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.alertsByTimeOfDay}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    fill="#8884d8"
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="timeOfDay"
                    label={({ timeOfDay }) => timeOfDay}
                  >
                    {stats.alertsByTimeOfDay.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} alerts`, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="mt-3 text-sm text-gray-600">
              <p className="flex items-start">
                <Info size={16} className="text-blue-500 mr-1 mt-0.5" />
                <span>
                  {(() => {
                    const maxTime = [...stats.alertsByTimeOfDay].sort((a, b) => b.count - a.count)[0];
                    return `Most alerts occur during ${maxTime.timeOfDay.toLowerCase()} hours (${maxTime.count}).`;
                  })()}
                </span>
              </p>
            </div>
          </div>
        </div>
        
        {/* Right Column: Trends and Risk Zones */}
        <div className="lg:col-span-1 space-y-6">
          {/* Alerts Over Time */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">Alerts Over Time</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={alertsOverTime}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#3b82f6" 
                    name="All Alerts"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="highSeverity" 
                    stroke="#ef4444" 
                    name="High Severity"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-sm text-gray-600">
              {stats.riskTrend === 'INCREASING' ? (
                <p className="flex items-start text-red-600">
                  <Activity size={16} className="mr-1 mt-0.5" />
                  <span>Alert frequency is trending upward. Review safety measures.</span>
                </p>
              ) : stats.riskTrend === 'DECREASING' ? (
                <p className="flex items-start text-green-600">
                  <Activity size={16} className="mr-1 mt-0.5" />
                  <span>Alert frequency is decreasing. Safety measures are working.</span>
                </p>
              ) : (
                <p className="flex items-start text-yellow-600">
                  <Activity size={16} className="mr-1 mt-0.5" />
                  <span>Alert frequency remains stable. Continue monitoring.</span>
                </p>
              )}
            </div>
          </div>
          
          {/* High Risk Zones */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">High Risk Areas</h3>
            
            {highRiskZones.length > 0 ? (
              <div className="space-y-3">
                {highRiskZones.slice(0, 3).map(zone => (
                  <div key={zone.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">{zone.location.room}</h4>
                      <span className="text-sm text-red-600 font-medium">
                        Risk Score: {zone.riskScore}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {zone.riskFactors.join(', ')}
                    </p>
                    <div className="mt-2 bg-gray-200 rounded-full h-2.5">
                      <div 
                        className={`h-2.5 rounded-full ${
                          zone.riskScore > 80 ? 'bg-red-500' :
                          zone.riskScore > 60 ? 'bg-yellow-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${zone.riskScore}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                      <span>Alerts: {zone.alertFrequency}/month</span>
                      <span className={`${
                        zone.trend === 'INCREASING' ? 'text-red-500' :
                        zone.trend === 'DECREASING' ? 'text-green-500' :
                        'text-yellow-500'
                      }`}>
                        {zone.trend === 'INCREASING' ? '↑' : 
                         zone.trend === 'DECREASING' ? '↓' : '→'} 
                        {zone.trend}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CheckCircle className="mx-auto text-green-500 mb-2" size={32} />
                <p className="text-gray-600">No high risk areas detected</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Bottom Section: Recent History */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Recent Alert History</h3>
        
        {alertHistory.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alert Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resolved
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resolution
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Response Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {alertHistory.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {item.alertType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityBadge(item.severity)}`}>
                        {item.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(item.resolvedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">
                      {item.resolution}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`${
                        item.timeToResolve < 15 ? 'text-green-600' :
                        item.timeToResolve < 30 ? 'text-yellow-600' :
                        'text-red-600'
                      } font-medium`}>
                        {item.timeToResolve} min
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-600">No recent alert history</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTab; 